/**
 * Centralized Business/B2B Context Detection
 *
 * This module provides a single source of truth for determining whether
 * the application is running in business/B2B mode.
 *
 * Priority Order:
 * 1. Environment variable (VITE_BUSINESS_MODE)
 * 2. Preview-build business override (?consumer=false on Fly PR previews)
 * 3. Subdomain detection (e.g., business.stippl.io, b2b.stippl.io)
 * 4. Default: consumer/B2C
 */

export interface BusinessConfig {
	isBusinessMode: boolean;
	businessSubdomain?: string;
	detectionMethod: "env" | "subdomain" | "none";
	isPresentationMode: boolean;
}

/**
 * Business-related subdomains that indicate B2B mode
 */
const BUSINESS_SUBDOMAINS = ["business", "b2b", "embed", "new", "future"];

/**
 * Extracts subdomain from hostname
 * @param hostname - The full hostname (e.g., 'business.stippl.io')
 * @returns The subdomain part (e.g., 'business') or null
 */
function getSubdomain(hostname: string): string | null {
	const parts = hostname.split(".");

	// Handle localhost and IP addresses
	if (parts.length < 3 || hostname.includes("localhost") || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
		return null;
	}

	// Return the first part as subdomain if not 'www'
	const subdomain = parts[0];
	return subdomain === "www" ? null : subdomain;
}

/**
 * Checks if the current subdomain indicates business mode
 * @param hostname - The current hostname
 * @returns true if subdomain indicates business mode
 */
function isBusinessSubdomain(hostname: string): boolean {
	const subdomain = getSubdomain(hostname);
	if (!subdomain) return false;

	const lower = subdomain.toLowerCase();
	return BUSINESS_SUBDOMAINS.includes(lower) || lower.startsWith("feat-b2b");
}

/**
 * Detects business mode from the environment variable
 * @returns true if VITE_BUSINESS_MODE is set to 'true' or '1'
 */
function isBusinessModeFromEnv(): boolean {
	const envMode = import.meta.env.VITE_BUSINESS_MODE;
	return envMode === "true" || envMode === "1";
}

/**
 * Main function to detect business context
 * This is the single source of truth for business mode detection
 *
 * @param location - Optional window.location object (defaults to current location)
 * @returns BusinessConfig object with mode and detection details
 */
export function detectBusinessContext(location?: Location): BusinessConfig {
	// SSR has no `window`, and this runs during render — BusinessProvider seeds its state with
	// `useState(() => detectBusinessContext())`, and entry-server.tsx wraps every marketing
	// page in that provider. An unguarded `window.location` therefore throws inside
	// renderToString, the handler catches it and falls through to the SPA, and marketing SSR
	// silently stops happening at all. That is exactly what shipped on 2026-07-22 (the 7-language
	// marketing commit, which added the provider to the SSR tree) and went unnoticed because the
	// fallback renders a correct page.
	//
	// B2C is the right server-side default: the flavor a request really belongs to is decided
	// from the Host header in marketingSsrHandler.ts and passed down as `hostFlavor`, so nothing
	// on the SSR path depends on this value. It only has to not throw, and to match what the
	// client computes on hydration for a b2c host — which it does.
	if (!location && typeof window === "undefined") {
		return { isBusinessMode: false, detectionMethod: "none", isPresentationMode: false };
	}
	const loc = location || window.location;
	const hostname = loc.hostname;
	const pathname = loc.pathname;

	// Check if in presentation mode (viewing /b/ share links)
	const isPresentationMode = pathname.startsWith("/b/");

	// 1. Check environment variable (highest priority)
	if (isBusinessModeFromEnv()) {
		return {
			isBusinessMode: true,
			detectionMethod: "env",
			isPresentationMode,
		};
	}

	// 2. Preview builds default to B2C — ?consumer=false opts into B2B mode for testing
	//    Only active when VITE_PREVIEW_MODE=true (set at build time in preview CI)
	//    Persists to sessionStorage so it survives navigation within the same tab
	if (import.meta.env.VITE_PREVIEW_MODE === "true") {
		const searchParams = new URLSearchParams(loc.search);
		if (searchParams.get("consumer") === "false") {
			try {
				sessionStorage.setItem("stippl_business_override", "true");
			} catch {}
		}
		if (searchParams.get("consumer") === "true") {
			try {
				sessionStorage.removeItem("stippl_business_override");
			} catch {}
		}
		let isBusinessOverride = false;
		try {
			isBusinessOverride = sessionStorage.getItem("stippl_business_override") === "true";
		} catch {}
		if (isBusinessOverride) {
			return {
				isBusinessMode: true,
				detectionMethod: "env",
				isPresentationMode,
			};
		}
		// No override → fall through to subdomain check / default (consumer/B2C)
	}

	// 3. Check subdomain
	if (isBusinessSubdomain(hostname)) {
		return {
			isBusinessMode: true,
			businessSubdomain: getSubdomain(hostname) || undefined,
			detectionMethod: "subdomain",
			isPresentationMode,
		};
	}

	// Default: not in business mode
	return {
		isBusinessMode: false,
		detectionMethod: "none",
		isPresentationMode,
	};
}

/**
 * Simple check if currently in business mode
 * @returns true if in business mode
 */
export function isBusinessMode(): boolean {
	return detectBusinessContext().isBusinessMode;
}

/**
 * Paths that render without requiring a signed-in user. Firebase auth
 * resolution can take several seconds on a cold visit (no persisted
 * IndexedDB state), so the App shell should not gate these behind it.
 */
const PUBLIC_ROUTE_PATTERN = /^\/(b|share|reel|join|login|register|forgotpassword|terms|privacy|disclaimer|business-terms|stays-terms|termsofservice|privacystatement|accept-invite|usermanagement)(\/|$)/;

/**
 * True when the current pathname is a route that does not require auth
 * and should render immediately, without waiting for Firebase to resolve.
 */
export function isPublicViewerRoute(pathname: string = window.location.pathname): boolean {
	return PUBLIC_ROUTE_PATTERN.test(pathname);
}

/**
 * Gets the business theme identifier
 * Useful for applying custom styles or branding
 *
 * @returns The theme identifier or 'default'
 */
export function getBusinessTheme(): string {
	const context = detectBusinessContext();
	return context.businessSubdomain || "stipplbusiness";
}
